import { Show, For, createMemo, createSignal, createEffect, onCleanup } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import wm from '../lib/wmStore.js';
import { gold, goldPerEach, qty, esoIconUrl, ESO_ICON_FALLBACK, qualityColor } from '../lib/format.js';
import { pickItem, pickUser, pickGuild } from '../lib/picks.js';
import { pickSettingOr } from '../lib/settingsLayers.js';
import { EMOJI } from '../lib/emoji.js';
import RelTime from '../components/RelTime.jsx';
import useCountUp from '../lib/useCountUp.js';

/**
 * LiveFeed.jsx — unified time-sorted activity stream.
 *
 * Issue #13: brings v1's Live Feed back into the v2 desktop. Settings
 * panel (per-widget) controls which lanes are visible and the
 * big-sale threshold.
 */
export default function LiveFeed(props) {
  const settings = () => props.settings || {};
  const { data, loading, error } = useWidgetData('live-feed', () => ({
    showSales:        pickSettingOr(props.settings, 'showSales', !!props.maximized, true),
    showBigSales:     pickSettingOr(props.settings, 'showBigSales', !!props.maximized, true),
    showSyncs:        pickSettingOr(props.settings, 'showSyncs', !!props.maximized, true),
    showPoints:       pickSettingOr(props.settings, 'showPoints', !!props.maximized, true),
    showRaffle:       props.compatSalesOnly ? false : settings().showRaffle !== false,
    showLeaderboard:  props.compatSalesOnly ? false : settings().showLeaderboard !== false,
    salesOnly:        !!props.compatSalesOnly,
    bigSaleThreshold: Number(settings().bigSaleThreshold) || 50000,
    item:    settings().item    || '',
    seller:  settings().seller  || '',
    guildId: settings().guildId || '',
    limit:   pickSettingOr(props.settings, 'limit', !!props.maximized, 80),
    spyGuildFilter: settings().spyGuildFilter || 'all',
  }));

  const events = () => data()?.events || [];
  const totalGold = createMemo(() => events()
    .filter(e => e.kind === 'sale' || e.kind === 'bigsale')
    .reduce((a, e) => a + (Number(e.price) || 0), 0));
  const saleCount = createMemo(() => events()
    .filter(e => e.kind === 'sale' || e.kind === 'bigsale').length);

  // #346 — client-side kind filters for sales / syncs / raffle / leaderboard chips,
  // persisted to widget settings so pinned desktop shortcuts remember chip state.
  const [filterSales,       setFilterSales]       = createSignal(settings().filterSales !== false);
  const [filterSyncs,       setFilterSyncs]       = createSignal(!props.compatSalesOnly && settings().filterSyncs !== false);
  const [filterRaffle,      setFilterRaffle]      = createSignal(!props.compatSalesOnly && settings().filterRaffle !== false);
  const [filterLeaderboard, setFilterLeaderboard] = createSignal(!props.compatSalesOnly && settings().filterLeaderboard !== false);

  function toggleChipFilter(key, sig, setter) {
    const next = !sig();
    setter(next);
    if (!props.widget?.id) return;
    try {
      wm.setSettings(props.widget.id, { ...(props.widget.settings || {}), [key]: next });
    } catch { /* best-effort */ }
  }

  const visibleEvents = createMemo(() => {
    const evts = events();
    if (filterSales() && filterSyncs() && filterRaffle() && filterLeaderboard()) return evts;
    return evts.filter(e => {
      if ((e.kind === 'sale' || e.kind === 'bigsale') && !filterSales()) return false;
      if (e.kind === 'sync'        && !filterSyncs())       return false;
      if (e.kind === 'raffle'      && !filterRaffle())      return false;
      if (e.kind === 'leaderboard' && !filterLeaderboard()) return false;
      return true;
    });
  });

  // Issue #109 — per-column toggles. Defaults to all-on so existing
  // dashboards behave unchanged. The four sale-row pieces (item, seller,
  // guild, price) plus the relative timestamp can each be hidden.
  const cols = createMemo(() => ({
    item:   settings().fieldItem   !== false,
    seller: settings().fieldSeller !== false,
    guild:  settings().fieldGuild  !== false,
    price:  settings().fieldPrice  !== false,
    time:   settings().fieldTime   !== false,
  }));

  const groupedSalesEnabled = createMemo(() => (
    !props.compatSalesOnly
    && cols().item
    && pickSettingOr(props.settings, 'groupSalesByItem', !!props.maximized, true)
  ));

  const displayEvents = createMemo(() => {
    const list = visibleEvents();
    if (!groupedSalesEnabled()) return list;

    const grouped = new Map();
    const merged = [];
    for (const ev of list) {
      if (ev.kind !== 'sale' && ev.kind !== 'bigsale') {
        merged.push(ev);
        continue;
      }
      const key = `${String(ev.itemName || '').toLowerCase()}|${ev.itemIcon || ''}|${ev.quality || ''}`;
      let group = grouped.get(key);
      if (!group) {
        group = {
          kind: 'sale-group',
          id: `sale-group-${key}`,
          timestamp: ev.timestamp || 0,
          itemName: ev.itemName || '—',
          itemIcon: ev.itemIcon || null,
          quality: ev.quality || null,
          totalGold: 0,
          avgPrice: 0,
          frequencyMs: null,
          hasBigSale: false,
          sales: [],
        };
        grouped.set(key, group);
        merged.push(group);
      }
      group.sales.push(ev);
      group.totalGold += Number(ev.price) || 0;
      if ((ev.timestamp || 0) > group.timestamp) group.timestamp = ev.timestamp || 0;
      if (ev.kind === 'bigsale') group.hasBigSale = true;
    }
    for (const group of grouped.values()) {
      group.sales.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      group.avgPrice = group.sales.length > 0
        ? (group.totalGold / group.sales.length)
        : 0;
      group.frequencyMs = estimateSaleFrequencyMs(group.sales);
    }
    merged.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    return merged;
  });

  // Issue #111 — animated count-ups for the stat strip so SSE-driven
  // refreshes feel kinetic instead of jarringly snapping to new totals.
  const totalGoldAnim = useCountUp(totalGold);
  const saleCountAnim = useCountUp(saleCount);

  // Issue #111 — delta-merge by stable id. Without this, every SSE
  // invalidate replaces the entire `events` array, which causes <For>
  // to remount every row and lose the slide-in animation cue. We track
  // which ids we've already shown and tag freshly-appearing ids with
  // `is-new` for the duration of one animation cycle.
  const [seenIds, setSeenIds] = createSignal(new Set());
  const [newIds, setNewIds] = createSignal(new Set());
  let firstFrame = true;
  let clearTimer = 0;
  onCleanup(() => {
    if (clearTimer) { clearTimeout(clearTimer); clearTimer = 0; }
  });
  createEffect(() => {
    const list = events();
    if (!Array.isArray(list)) return;
    const seen = seenIds();
    const fresh = new Set();
    for (const e of list) {
      const id = e.id;
      if (!id) continue;
      if (!seen.has(id)) fresh.add(id);
    }
    if (fresh.size === 0) {
      // First load: prime the seen set without animating anything.
      if (firstFrame) {
        const next = new Set(seen);
        for (const e of list) if (e.id) next.add(e.id);
        setSeenIds(next);
        firstFrame = false;
      }
      return;
    }
    const next = new Set(seen);
    for (const id of fresh) next.add(id);
    if (next.size > 500) {
      const keep = Array.from(next).slice(-400);
      setSeenIds(new Set(keep));
    } else {
      setSeenIds(next);
    }
    if (firstFrame) {
      // Don't animate the very first batch the user sees.
      firstFrame = false;
      return;
    }
    setNewIds(fresh);
    if (clearTimer) clearTimeout(clearTimer);
    clearTimer = setTimeout(() => setNewIds(new Set()), 600);
  });

  return (
    <div class="v2-widget v2-livefeed">
      <Show when={!loading()} fallback={<div class="v2-widget-skel">Loading…</div>}>
        <Show when={!error()} fallback={<div class="v2-error">{error()}</div>}>
          <div class="v2-stat-grid">
            <div class="v2-stat v2-stat--gold">
              <span class="v2-stat__label">Gold (window)</span>
              <span class="v2-stat__value">{gold(Math.round(totalGoldAnim()))}</span>
            </div>
            <div class="v2-stat v2-stat--cyan">
              <span class="v2-stat__label">Sales shown</span>
              <span class="v2-stat__value">{qty(Math.round(saleCountAnim()))}</span>
            </div>
          </div>
          <Show when={events().length} fallback={<div class="v2-empty">No events match these filters.</div>}>
            <div class="v2-feed-chips" aria-label="Live Feed quick filters">
              <button
                class={`v2-gt-chip${filterSales() ? ' is-on' : ''}`}
                style={{ 'font-size': '11px' }}
                onClick={() => toggleChipFilter('filterSales', filterSales, setFilterSales)}
              >{EMOJI.sales} Items</button>
              <Show when={!props.compatSalesOnly}>
                <button
                  class={`v2-gt-chip${filterSyncs() ? ' is-on' : ''}`}
                  style={{ 'font-size': '11px' }}
                  onClick={() => toggleChipFilter('filterSyncs', filterSyncs, setFilterSyncs)}
                >{EMOJI.sync || '📡'} Syncs</button>
                <button
                  class={`v2-gt-chip${filterRaffle() ? ' is-on' : ''}`}
                  style={{ 'font-size': '11px' }}
                  onClick={() => toggleChipFilter('filterRaffle', filterRaffle, setFilterRaffle)}
                >{EMOJI.raffle} Raffle</button>
                <button
                  class={`v2-gt-chip${filterLeaderboard() ? ' is-on' : ''}`}
                  style={{ 'font-size': '11px' }}
                  onClick={() => toggleChipFilter('filterLeaderboard', filterLeaderboard, setFilterLeaderboard)}
                >{EMOJI.trophy} Leaderboard</button>
              </Show>
            </div>
            <Show when={displayEvents().length} fallback={<div class="v2-empty">All feed lanes are hidden — toggle a filter chip above to view events.</div>}>
              <div class="v2-widget__scroll">
                <For each={displayEvents()}>{(entry) => (
                  entry.kind === 'sale-group'
                    ? <SaleGroupRow group={entry} cols={cols()} isNew={entry.sales.some(s => newIds().has(s.id))} />
                    : <FeedRow ev={entry} cols={cols()} isNew={newIds().has(entry.id)} />
                )}</For>
              </div>
            </Show>
          </Show>
        </Show>
      </Show>
    </div>
  );
}

function FeedRow(props) {
  const ev = props.ev;
  const cols = props.cols || { item: true, seller: true, guild: true, price: true, time: true };
  const newCls = props.isNew ? ' is-new' : '';
  if (ev.kind === 'sale' || ev.kind === 'bigsale') {
    return (
      <div class={`v2-feed-row v2-feed-row--${ev.kind}${newCls}`}>
        <span class="v2-feed-row__icon" title={ev.kind === 'bigsale' ? 'Big sale' : 'Sale'}>
          {ev.kind === 'bigsale' ? EMOJI.bigsale : EMOJI.sales}
        </span>
        <Show when={cols.time}>
          <RelTime class="v2-feed-row__time" ts={ev.timestamp} />
        </Show>
        <span class="v2-feed-row__main v2-feed-row__main--sale">
          <span class="v2-feed-sale-line v2-feed-sale-line--item">
            <Show when={cols.item}>
              <Show when={ev.itemIcon}>
                <img class="v2-item-icon v2-item-icon--xs" src={esoIconUrl(ev.itemIcon)} alt=""
                  onError={(el) => { el.currentTarget.src = ESO_ICON_FALLBACK; }} />
              </Show>
              <span class="v2-pick v2-pick--item" data-icon={ev.itemIcon || ''} data-pick-key={ev.itemName || ''}
                style={ev.quality ? { color: qualityColor(ev.quality) } : undefined}
                onClick={() => pickItem(ev.itemName, { icon: ev.itemIcon })}>{ev.itemName || '—'}</span>
              {ev.quant > 1 ? <em class="v2-feed-sale-line__qty">×{ev.quant}</em> : null}
            </Show>
            <Show when={!cols.item && cols.seller && ev.sellerName}>
              <b class="v2-pick v2-pick--user" data-pick-key={ev.sellerName} onClick={() => pickUser(ev.sellerName)}>{ev.sellerName}</b>
            </Show>
          </span>
          <Show when={(cols.seller && ev.sellerName && cols.item) || (cols.guild && ev.guildName)}>
            <span class="v2-feed-sale-line v2-feed-sale-line--meta">
              <Show when={cols.seller && ev.sellerName && cols.item}>
                <b class="v2-pick v2-pick--user" data-pick-key={ev.sellerName} onClick={() => pickUser(ev.sellerName)}>{ev.sellerName}</b>
              </Show>
              <Show when={cols.guild && ev.guildName}>
                <span class="v2-feed-sale-line__dot">•</span>
                <span class="v2-feed-row__guild v2-pick v2-pick--guild" data-pick-key={ev.guildName} onClick={() => pickGuild(ev.guildName)}>{ev.guildName}</span>
              </Show>
            </span>
          </Show>
        </span>
        <Show when={cols.price}>
          <span class="v2-feed-row__price">
            {gold(ev.price)}
            {ev.quant > 1 ? <em class="v2-sale-row__per"> {goldPerEach(ev.price, ev.quant)}</em> : null}
          </span>
        </Show>
      </div>
    );
  }
  if (ev.kind === 'sync') {
    const sync = describeSyncSource(ev.source);
    const syncMeta = [];
    if (ev.totalItems != null && ev.totalItems > 0) syncMeta.push(`${qty(ev.totalItems)} items indexed`);
    if (ev.totalSales != null && ev.totalSales > 0) syncMeta.push(`${qty(ev.totalSales)} sales recorded`);
    if (ev.totalPriceRows != null && ev.totalPriceRows > 0) syncMeta.push(`${qty(ev.totalPriceRows)} price rows refreshed`);
    if (ev.operator) syncMeta.push(`by ${ev.operator}`);
    return (
      <div class={`v2-feed-row v2-feed-row--sync${newCls}`}>
        <span class="v2-feed-row__icon" title="Sync">📡</span>
        <Show when={cols.time}>
          <RelTime class="v2-feed-row__time" ts={ev.timestamp} />
        </Show>
        <span class="v2-feed-row__main v2-feed-row__main--sync">
          <span class="v2-feed-sync__title">
            <span class="v2-feed-sync__badge">{sync.badge}</span>
            <b>{sync.label}</b>
          </span>
          <span class="v2-feed-sync__meta">{syncMeta.join(' · ') || 'Background sync finished'}</span>
        </span>
      </div>
    );
  }
  if (ev.kind === 'points') {
    return (
      <div class={`v2-feed-row v2-feed-row--points${newCls}`}>
        <span class="v2-feed-row__icon" title="Sync points">⭐</span>
        <Show when={cols.time}>
          <RelTime class="v2-feed-row__time" ts={ev.timestamp} />
        </Show>
        <span class="v2-feed-row__main">
          <Show when={ev.earner}>
            <b class="v2-pick v2-pick--user" onClick={() => pickUser(ev.earner)}>{ev.earner}</b>{' · '}
          </Show>
          {ev.body || `+${qty(ev.points || 0)} sync points`}
        </span>
      </div>
    );
  }
  if (ev.kind === 'raffle') {
    return (
      <div class={`v2-feed-row v2-feed-row--raffle${newCls}`}>
        <span class="v2-feed-row__icon" title="Raffle winner">{EMOJI.raffle}</span>
        <Show when={cols.time}>
          <RelTime class="v2-feed-row__time" ts={ev.timestamp} />
        </Show>
        <span class="v2-feed-row__main">
          <b>{ev.guildLabel || 'Raffle'}</b> · place {ev.place || '—'}
          <Show when={ev.user_tag}> · <span class="v2-pick v2-pick--user" onClick={() => pickUser(ev.user_tag)}>{ev.user_tag}</span></Show>
          <Show when={ev.prize}> · {gold(ev.prize)}</Show>
        </span>
      </div>
    );
  }
  if (ev.kind === 'leaderboard') {
    return (
      <div class={`v2-feed-row v2-feed-row--leaderboard${newCls}`}>
        <span class="v2-feed-row__icon" title="Leaderboard">{EMOJI.trophy}</span>
        <Show when={cols.time}>
          <RelTime class="v2-feed-row__time" ts={ev.timestamp} />
        </Show>
        <span class="v2-feed-row__main">{ev.body || 'Leaderboard updated'}</span>
      </div>
    );
  }
  return null;
}

function SaleGroupRow(props) {
  const group = props.group || {};
  const cols = props.cols || { item: true, seller: true, guild: true, price: true, time: true };
  const newCls = props.isNew ? ' is-new' : '';
  const salesCount = group.sales?.length || 0;
  const frequencyLabel = saleFrequencyLabel(group.frequencyMs, salesCount);
  return (
    <div class={`v2-feed-group${group.hasBigSale ? ' v2-feed-group--hot' : ''}${newCls}`}>
      <div class="v2-feed-group__head">
        <span class="v2-feed-row__icon" title={group.hasBigSale ? 'Contains hot sale(s)' : 'Sales group'}>
          {group.hasBigSale ? EMOJI.bigsale : EMOJI.sales}
        </span>
        <Show when={group.itemIcon}>
          <img class="v2-item-icon v2-item-icon--xs" src={esoIconUrl(group.itemIcon)} alt=""
            onError={(el) => { el.currentTarget.src = ESO_ICON_FALLBACK; }} />
        </Show>
        <span class="v2-pick v2-pick--item" data-icon={group.itemIcon || ''} data-pick-key={group.itemName || ''}
          style={group.quality ? { color: qualityColor(group.quality) } : undefined}
          onClick={() => pickItem(group.itemName, { icon: group.itemIcon })}>
          <span class="v2-feed-group__item">{group.itemName || '—'}</span>
        </span>
        <span class="v2-feed-group__meta">
          <span class="v2-feed-group__count">{qty(salesCount)} sales</span>
          <Show when={cols.price}>
            <span class="v2-feed-group__gold" title="Average sale price from recent grouped sales">
              <span class="v2-feed-group__metric-label">AVG</span>{gold(Math.round(group.avgPrice || 0))}
            </span>
          </Show>
          <Show when={cols.time}>
            <span class="v2-feed-group__freq" title="Estimated sale frequency from recent grouped sales">{frequencyLabel}</span>
          </Show>
        </span>
      </div>
      <div class="v2-feed-group__rows">
        <span class="v2-feed-group__rows-label">{cols.seller ? 'Recent sellers' : 'Recent sales'}</span>
        <For each={group.sales || []}>{(sale) => {
          const guild = guildBadge(sale.guildName);
          return (
            <div class={`v2-feed-group__row${sale.kind === 'bigsale' ? ' is-hot' : ''}`}>
              <Show when={cols.guild}>
                <span class={`v2-feed-group__guild${guild ? ` is-${guild.key}` : ''}`} title={guild?.title || sale.guildName || ''}>
                  {guild?.label || '—'}
                </span>
              </Show>
              <Show when={cols.seller && sale.sellerName}>
                <span class="v2-feed-group__seller v2-pick v2-pick--user" data-pick-key={sale.sellerName} onClick={() => pickUser(sale.sellerName)}>
                  {sale.sellerName}
                </span>
              </Show>
              <Show when={cols.price}>
                <span class="v2-feed-group__price">{gold(sale.price || 0)}</span>
                <span class="v2-feed-group__stack">
                  {sale.quant > 1 ? `×${sale.quant} · ${goldPerEach(sale.price, sale.quant)} ea` : ''}
                </span>
              </Show>
              <Show when={cols.time}>
                <RelTime class="v2-feed-group__time" ts={sale.timestamp} />
              </Show>
            </div>
          );
        }}</For>
      </div>
    </div>
  );
}

function estimateSaleFrequencyMs(sales) {
  if (!Array.isArray(sales) || sales.length < 2) return null;
  const deltas = [];
  for (let i = 0; i < sales.length - 1; i += 1) {
    const newerTs = Number(sales[i]?.timestamp) || 0;
    const olderTs = Number(sales[i + 1]?.timestamp) || 0;
    const delta = newerTs - olderTs;
    if (delta > 0) deltas.push(delta);
    if (deltas.length >= 5) break;
  }
  if (!deltas.length) return null;
  return Math.round(deltas.reduce((sum, ms) => sum + ms, 0) / deltas.length);
}

function saleFrequencyLabel(frequencyMs, salesCount) {
  if (!frequencyMs || frequencyMs <= 0) {
    return salesCount > 1 ? 'bursty' : 'single sale';
  }
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (frequencyMs < minute) return 'every <1m';
  if (frequencyMs < hour) return `every ${Math.round(frequencyMs / minute)}m`;
  if (frequencyMs < day) {
    const hrs = frequencyMs / hour;
    return `every ${hrs >= 10 ? Math.round(hrs) : hrs.toFixed(1)}h`;
  }
  return `every ${Math.round(frequencyMs / day)}d`;
}

function describeSyncSource(source) {
  const raw = String(source || '').trim();
  const lower = raw.toLowerCase();
  if (lower === 'mm') return { badge: 'MM', label: 'Marketplace sync' };
  if (lower === 'ttc') return { badge: 'TTC', label: 'Tamriel Trade Centre refresh' };
  if (lower.startsWith('mm:')) {
    const key = lower.slice(3);
    const guild = key === 'post'
      ? 'Redfur Post'
      : key === 'caravan'
        ? 'Redfur Caravan'
        : key === 'dealers'
          ? 'Redfur Dealers'
          : raw.slice(3);
    return { badge: 'MM', label: `${guild} sync` };
  }
  if (lower.startsWith('raffle:')) {
    const key = lower.slice(7);
    const guild = key === 'post'
      ? 'Post'
      : key === 'dealers'
        ? 'Dealers'
        : raw.slice(7);
    return { badge: 'RAFFLE', label: `${guild} raffle snapshot` };
  }
  return { badge: 'SYNC', label: raw || 'Sync update' };
}

function guildBadge(guildName) {
  const name = String(guildName || '').toLowerCase();
  if (name.includes('post')) return { key: 'post', label: 'POST', title: guildName || 'Redfur Post' };
  if (name.includes('caravan')) return { key: 'caravan', label: 'CRVN', title: guildName || 'Redfur Caravan' };
  if (name.includes('dealers')) return { key: 'dealers', label: 'DLRS', title: guildName || 'Redfur Dealers' };
  return null;
}
